import { useState, type FormEvent } from 'react';
import { ApiError, api } from './api.js';
import { Button, Field, Input, cx } from './ui.js';

export interface VehicleFormValues {
  type: 'car' | 'moto';
  plate: string;
  model: string;
  color: string;
  year: string;
}

const EMPTY: VehicleFormValues = { type: 'car', plate: '', model: '', color: '', year: '' };

/**
 * Cadastro e edição do veículo são o mesmo formulário: `POST
 * /api/driver/vehicle` faz upsert pela placa. Serve tanto ao passo 2 do
 * cadastro do motorista quanto a "adicionar carro" na frota — os dois
 * mandam o mesmo corpo para a mesma rota.
 */
export function VehicleForm({
  initial,
  submitLabel = 'Salvar veículo',
  onDone,
  onError,
}: {
  initial?: Partial<VehicleFormValues>;
  submitLabel?: string;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [values, setValues] = useState<VehicleFormValues>({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api('/api/driver/vehicle', {
        body: { ...values, year: Number(values.year) },
      });
      onDone();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'não foi possível salvar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {(['car', 'moto'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setValues((v) => ({ ...v, type: t }))}
            className={cx(
              'rounded-2xl border p-4 font-semibold transition',
              values.type === t ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted',
            )}
          >
            {t === 'car' ? 'Carro' : 'Moto'}
          </button>
        ))}
      </div>

      <Field label="Placa">
        <Input
          value={values.plate}
          onChange={(e) => setValues((v) => ({ ...v, plate: e.target.value.toUpperCase() }))}
          required
          placeholder="ABC1D23"
          autoCapitalize="characters"
        />
      </Field>
      <Field label="Modelo">
        <Input
          value={values.model}
          onChange={(e) => setValues((v) => ({ ...v, model: e.target.value }))}
          required
          placeholder="Onix"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cor">
          <Input
            value={values.color}
            onChange={(e) => setValues((v) => ({ ...v, color: e.target.value }))}
            required
            placeholder="Prata"
          />
        </Field>
        <Field label="Ano">
          <Input
            value={values.year}
            onChange={(e) => setValues((v) => ({ ...v, year: e.target.value }))}
            inputMode="numeric"
            required
            placeholder="2020"
          />
        </Field>
      </div>
      <Button type="submit" full loading={busy}>
        {submitLabel}
      </Button>
    </form>
  );
}
